import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable } from 'rxjs';
import {
    impersonationSessionStorageKey,
    impersonationTokenHeaderName,
    type ImpersonationSession
} from '../../domains/auth';
import {
    GlobalConfigService,
    StorageService
} from '../services';

export function impersonationInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    const globalConfigService = inject(GlobalConfigService);
    const storageService = inject(StorageService);
    const requestUrl = getRequestUrl(req.url, globalConfigService.apiUrl);

    if (!requestUrl || !isApiRequest(requestUrl, globalConfigService.apiUrl) || shouldSkipImpersonation(requestUrl)) {
        return next(req);
    }

    const session = storageService.getItem<ImpersonationSession>(impersonationSessionStorageKey, true);

    if (!session?.token || session.expiresAt <= Date.now()) {
        return next(req);
    }

    // Only normal API calls receive the impersonation token; auth lifecycle calls stay bound to the actor cookie.
    return next(req.clone({
        headers: req.headers.set(impersonationTokenHeaderName, session.token)
    }));
}

function getRequestUrl(url: string, apiUrl: string): URL | null {
    try {
        return new URL(url, apiUrl);
    } catch {
        return null;
    }
}

function isApiRequest(requestUrl: URL, apiUrl: string): boolean {
    try {
        return requestUrl.origin === new URL(apiUrl).origin;
    } catch {
        return false;
    }
}

function shouldSkipImpersonation(requestUrl: URL): boolean {
    const path = requestUrl.pathname.replace(/^\/+/, '').toLowerCase();

    return path.startsWith('identity/login') ||
        path.startsWith('identity/logout') ||
        path.startsWith('identity/clear-auth-cookies') ||
        path.startsWith('identity/refresh') ||
        path.startsWith('api/internal/impersonation-sessions');
}
